import { api } from './client';

export type MonitoringTargetType = 'linux' | 'windows' | 'mikrotik' | 'ups' | 'other';
export type MonitoringTargetLifecycle = 'pending' | 'active' | 'suspended' | 'decommissioned';

export interface MonitoringTargetRecord {
  id: string;
  client_id: number;
  type: MonitoringTargetType;
  display_name: string;
  lifecycle: MonitoringTargetLifecycle;
  version: number;
  aggregate_revision: number;
  created_at: string;
  updated_at: string;
}

export interface MonitoringTargetPage {
  items: MonitoringTargetRecord[];
  next_cursor: string | null;
}

export type MonitoringEnrollmentPresentation =
  | 'awaiting_approval'
  | 'awaiting_agent'
  | 'binding'
  | 'complete'
  | 'cancelled'
  | 'expired'
  | 'manual_recovery_required'
  | 'temporarily_unavailable';

export interface MonitoringEnrollmentView {
  intent: {
    id: string;
    target_id: string;
    client_id: number;
    registration_request_id: string;
    app_id: string;
    installation_id: string;
    status: 'open' | 'bound' | 'cancelled' | 'expired';
    request_expires_at: string;
    enrollment_expires_at: string | null;
    approval_phase: 'none' | 'dispatching' | 'approved';
    cancellation_phase: 'none' | 'dispatching' | 'cancelled';
    version: number;
    approved_at: string | null;
    consumed_station_id: string | null;
    licensing_relation_revision: string | null;
    bound_station_id: string | null;
    binding_id: string | null;
    created_at: string;
    updated_at: string;
  };
  registration: {
    status: 'pending' | 'approved' | 'consumed' | 'expired' | 'rejected' | 'revoked' | 'unavailable';
    request_expires_at: string | null;
    enrollment_expires_at: string | null;
  };
  binding: {
    status: 'bound' | 'unbound';
    binding_id: string | null;
    station_id: string | null;
  };
  presentation: MonitoringEnrollmentPresentation;
  retryable: boolean;
  error_code: string | null;
}

export async function listMonitoringTargets(clientId: number): Promise<MonitoringTargetPage> {
  return api.get<MonitoringTargetPage>('/v1/monitoring/targets', {
    params: { client_id: clientId, limit: 100 },
  }).then((response) => response.data);
}

export async function createMonitoringTarget(input: {
  clientId: number;
  displayName: string;
  requestId: string;
}): Promise<MonitoringTargetRecord> {
  return api.post<MonitoringTargetRecord>('/v1/monitoring/targets', {
    client_id: input.clientId,
    type: 'linux',
    display_name: input.displayName,
    request_id: input.requestId,
  }).then((response) => response.data);
}

// All intent mutations use the server-authoritative /v1/monitoring/enrollment-intents boundary.
export async function createMonitoringEnrollmentIntent(input: {
  targetId: string;
  registrationLocator: string;
  requestId: string;
}): Promise<MonitoringEnrollmentView> {
  return api.post<MonitoringEnrollmentView>('/v1/monitoring/enrollment-intents', {
    target_id: input.targetId,
    registration_locator: input.registrationLocator,
    request_id: input.requestId,
  }).then((response) => response.data);
}

export async function getMonitoringEnrollmentIntent(intentId: string): Promise<MonitoringEnrollmentView> {
  return api.get<MonitoringEnrollmentView>(
    `/v1/monitoring/enrollment-intents/${encodeURIComponent(intentId)}`,
  ).then((response) => response.data);
}

export async function approveMonitoringEnrollmentIntent(input: {
  intentId: string;
  expectedVersion: number;
  requestId: string;
}): Promise<MonitoringEnrollmentView> {
  return api.post<MonitoringEnrollmentView>(
    `/v1/monitoring/enrollment-intents/${encodeURIComponent(input.intentId)}/approve`,
    { expected_version: input.expectedVersion, request_id: input.requestId },
  ).then((response) => response.data);
}

export async function retryMonitoringEnrollmentIntent(input: {
  intentId: string;
  expectedVersion: number;
  requestId: string;
}): Promise<MonitoringEnrollmentView> {
  return api.post<MonitoringEnrollmentView>(
    `/v1/monitoring/enrollment-intents/${encodeURIComponent(input.intentId)}/retry`,
    { expected_version: input.expectedVersion, request_id: input.requestId },
  ).then((response) => response.data);
}

export async function cancelMonitoringEnrollmentIntent(input: {
  intentId: string;
  expectedVersion: number;
  requestId: string;
}): Promise<MonitoringEnrollmentView> {
  return api.post<MonitoringEnrollmentView>(
    `/v1/monitoring/enrollment-intents/${encodeURIComponent(input.intentId)}/cancel`,
    {
      expected_version: input.expectedVersion,
      request_id: input.requestId,
      reason: 'operator_cancelled',
    },
  ).then((response) => response.data);
}
